import React from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import * as styles from "./ForklyWidget.module.css";
import itemImageStyle from "../../utils/itemImageStyle";
import { chipVariant, isToggleChip, hintText } from "./forklyUi";
import { VOICE_STATE } from "./useVoiceOrder";

const SEND_ARROW = "➜";

const variantClass = (variant) =>
  variant === "primary" ? styles.optPrimary : variant === "ghost" ? styles.optGhost : "";

const InputField = ({
  input,
  inputRef,
  inputValue,
  loading,
  chips,
  onChange,
  onKeyDown,
  onSubmit,
  onChip,
  voiceState,
  onMicStart,
  onMicStop,
}) => {
  const isRecording = voiceState === VOICE_STATE.RECORDING;
  const isProcessing = voiceState === VOICE_STATE.PROCESSING;

  return (
    <>
      <div className={styles.field}>
        <Button
          unstyled
          className={`${styles.micInlineBtn} ${isRecording ? styles.micInlineBtnActive : ""}`}
          onClick={isRecording ? onMicStop : onMicStart}
          disabled={isProcessing}
          aria-label={isRecording ? "Stop" : "Dictate"}
          icon={`pi ${isRecording ? "pi-stop-circle" : isProcessing ? "pi-spin pi-spinner" : "pi-microphone"}`}
        />
        <InputText
          unstyled
          ref={inputRef}
          className={styles.fieldInput}
          placeholder={input.placeholder || "Type or speak your answer…"}
          value={inputValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          maxLength={input.maxLength || 500}
          disabled={loading}
        />
        <Button
          unstyled
          className={styles.send}
          label={SEND_ARROW}
          onClick={onSubmit}
          disabled={!inputValue.trim() || loading}
          aria-label="Send"
        />
      </div>
      {chips.some((c) => c.action === "skip") && (
        <div className={styles.opts}>
          {chips
            .filter((c) => c.action === "skip")
            .map((c) => (
              <Button
                unstyled
                key={`skip-${c.value}`}
                className={`${styles.opt} ${styles.optGhost}`}
                label={c.label}
                onClick={() => onChip(c)}
              />
            ))}
        </div>
      )}
    </>
  );
};

const ItemList = ({ items, multi, onChip }) => (
  <div className={styles.itemList}>
    {items.map((item) => (
      <Button
        unstyled
        key={`item-${item.value}`}
        className={`${styles.itemRow} ${item.selected ? styles.itemRowSelected : ""}`}
        onClick={() => onChip(item)}
      >
        <div className={styles.itemImage} style={itemImageStyle(item.image_name)} />
        <div className={styles.itemName}>{item.label}</div>
        {multi && (
          <i
            className={`pi ${
              item.selected ? "pi-check-circle" : "pi-circle"
            } ${styles.itemIcon}`}
          />
        )}
      </Button>
    ))}
  </div>
);

const ChipRow = ({ chips, multi, stepId, onChip }) => (
  <div className={`${styles.opts} ${multi ? styles.optsStart : ""}`}>
    {chips.map((chip) =>
      isToggleChip(chip, multi) ? (
        <Button
          unstyled
          key={`chip-${chip.value}`}
          className={`${styles.chip} ${chip.selected ? styles.chipSel : ""}`}
          label={chip.label}
          icon={chip.selected ? "pi pi-times" : undefined}
          iconPos="right"
          onClick={() => onChip(chip)}
        />
      ) : (
        <Button
          unstyled
          key={`chip-${chip.value}`}
          className={`${styles.opt} ${variantClass(chipVariant(chip, stepId))}`}
          label={chip.label}
          onClick={() => onChip(chip)}
        />
      ),
    )}
  </div>
);

const ForklyDock = ({
  showInput,
  showItems,
  showChips,
  input,
  inputRef,
  inputValue,
  loading,
  chips,
  items,
  multi,
  multiMax,
  stepId,
  inputError,
  onInputChange,
  onKeyDown,
  onSubmit,
  onChip,
  voiceState,
  transcript,
  voiceError,
  showMicButton,
  onMicStart,
  onMicStop,
  onMicReset,
  inSession,
  onStartSession,
  optionsHidden,
  voiceOptions,
  heardValue,
  pendingMatch,
  onConfirmMatch,
  onDismissMatch,
}) => {
  const isProcessing = voiceState === VOICE_STATE.PROCESSING;

  return (
    <div className={styles.dock}>
      {showInput ? (
        <InputField
          input={input}
          inputRef={inputRef}
          inputValue={inputValue}
          loading={loading}
          chips={chips}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onSubmit={onSubmit}
          onChip={onChip}
          voiceState={voiceState}
          onMicStart={onMicStart}
          onMicStop={onMicStop}
        />
      ) : optionsHidden && voiceOptions.length > 0 ? (
        <div className={styles.voiceOptions}>
          <div className={styles.voiceOptionsLead}>
            <i className="pi pi-microphone" />
            <span>{multi ? "Say any of these, then “done”:" : "Just say one:"}</span>
          </div>
          <div className={styles.voiceOptionsList}>
            {voiceOptions.map((o) => {
              const heard = o.value === heardValue;
              return (
                <span
                  key={`vo-${o.value}`}
                  className={`${styles.voiceOption} ${o.selected ? styles.voiceOptionSel : heard ? styles.voiceOptionHeard : ""}`}
                >
                  {(o.selected || heard) && <i className="pi pi-check" />}
                  {o.label}
                </span>
              );
            })}
          </div>
          {heardValue && (
            <div className={styles.voiceHeardHint}>
              Got it — say <strong>“no”</strong> to change.
            </div>
          )}
        </div>
      ) : (
        <>
          {showItems && <ItemList items={items} multi={multi} onChip={onChip} />}
          {showChips && (
            <ChipRow chips={chips} multi={multi} stepId={stepId} onChip={onChip} />
          )}
          {multi && <div className={styles.hint}>{hintText(multiMax)}</div>}
        </>
      )}

      {showMicButton && !showInput && stepId !== "ask_order_mode" && (
        <div className={styles.voiceBar}>
          {inSession ? (
            <div
              className={`${styles.micStatus} ${isProcessing ? styles.micStatusProcessing : styles.micStatusActive}`}
            >
              <i className={`pi ${isProcessing ? "pi-spin pi-spinner" : "pi-microphone"}`} />
              <span>{isProcessing ? "Recognizing…" : "Listening…"}</span>
            </div>
          ) : (
            <>
              <Button
                unstyled
                className={styles.micBtn}
                onClick={onStartSession}
                aria-label="Speak your order"
              >
                <i className="pi pi-microphone" />
                <span>Speak your order</span>
              </Button>
              {(voiceState === VOICE_STATE.DONE || voiceState === VOICE_STATE.ERROR) && (
                <Button
                  unstyled
                  className={styles.micReset}
                  onClick={onMicReset}
                  aria-label="Reset voice"
                  icon="pi pi-times"
                />
              )}
            </>
          )}
        </div>
      )}

      {transcript && voiceState === VOICE_STATE.DONE && (
        <div className={styles.voiceTranscript}>
          <i className="pi pi-volume-up" /> &ldquo;{transcript}&rdquo;
        </div>
      )}

      {voiceError && voiceState === VOICE_STATE.ERROR && (
        <div className={styles.voiceError}>{voiceError}</div>
      )}

      {pendingMatch && (
        <div className={styles.voiceConfirm}>
          <span className={styles.voiceConfirmText}>
            Did you mean <strong>{pendingMatch.label}</strong>?
          </span>
          <div className={styles.voiceConfirmBtns}>
            <Button
              unstyled
              className={styles.voiceConfirmYes}
              label="Yes"
              icon="pi pi-check"
              onClick={onConfirmMatch}
            />
            <Button
              unstyled
              className={styles.voiceConfirmNo}
              label="No"
              icon="pi pi-times"
              onClick={onDismissMatch}
            />
          </div>
        </div>
      )}

      {inputError && <div className={styles.errorBar}>{inputError}</div>}
    </div>
  );
};

export default ForklyDock;
